import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  OrderProcessingState,
  OrderProcessingListResponse,
  OrderProcessingStatsResponse,
  OrderProcessingSingleResponse,
} from "../../types/orderProcessing";
import {
  getOrderProcessingList,
  searchOrderProcessing,
  getOrderProcessingStats,
  getOrderProcessingSingle,
  updateOrderProcessingStatus,
} from "../thunk";

const initialState: OrderProcessingState = {
  orders: {
    data: [],
    pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
    status: "idle",
    error: null,
  },
  stats: {
    data: null,
    status: "idle",
    error: null,
  },
  singleOrder: {
    data: null,
    status: "idle",
    error: null,
  },
  updateStatus: {
    status: "idle",
    error: null,
  },
};

const orderProcessingSlice = createSlice({
  name: "orderProcessing",
  initialState,
  reducers: {
    resetUpdateStatus(state) {
      state.updateStatus.status = "idle";
      state.updateStatus.error = null;
    },
    clearSingleOrder(state) {
      state.singleOrder.data = null;
      state.singleOrder.status = "idle";
      state.singleOrder.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderProcessingList.pending, (state) => {
        state.orders.status = "loading";
        state.orders.error = null;
      })
      .addCase(
        getOrderProcessingList.fulfilled,
        (state, action: PayloadAction<OrderProcessingListResponse>) => {
          state.orders.status = "success";
          state.orders.data = action.payload.data;
          state.orders.pagination = action.payload.pagination;
          state.orders.error = null;
        },
      )
      .addCase(getOrderProcessingList.rejected, (state, action) => {
        state.orders.status = "failed";
        state.orders.error =
          (action.payload as string) || "Failed to fetch orders";
      })

      .addCase(searchOrderProcessing.pending, (state) => {
        state.orders.status = "loading";
        state.orders.error = null;
      })
      .addCase(
        searchOrderProcessing.fulfilled,
        (state, action: PayloadAction<OrderProcessingListResponse>) => {
          state.orders.status = "success";
          state.orders.data = action.payload.data;
          state.orders.pagination = action.payload.pagination;
          state.orders.error = null;
        },
      )
      .addCase(searchOrderProcessing.rejected, (state, action) => {
        state.orders.status = "failed";
        state.orders.error =
          (action.payload as string) || "Failed to search orders";
      })

      .addCase(getOrderProcessingStats.pending, (state) => {
        state.stats.status = "loading";
        state.stats.error = null;
      })
      .addCase(
        getOrderProcessingStats.fulfilled,
        (state, action: PayloadAction<OrderProcessingStatsResponse>) => {
          state.stats.status = "success";
          state.stats.data = action.payload.data;
          state.stats.error = null;
        },
      )
      .addCase(getOrderProcessingStats.rejected, (state, action) => {
        state.stats.status = "failed";
        state.stats.error =
          (action.payload as string) || "Failed to fetch statistics";
      })

      .addCase(getOrderProcessingSingle.pending, (state) => {
        state.singleOrder.status = "loading";
        state.singleOrder.error = null;
      })
      .addCase(
        getOrderProcessingSingle.fulfilled,
        (state, action: PayloadAction<OrderProcessingSingleResponse>) => {
          state.singleOrder.status = "success";
          state.singleOrder.data = action.payload.data;
          state.singleOrder.error = null;
        },
      )
      .addCase(getOrderProcessingSingle.rejected, (state, action) => {
        state.singleOrder.status = "failed";
        state.singleOrder.error =
          (action.payload as string) || "Failed to fetch order";
      })

      .addCase(updateOrderProcessingStatus.pending, (state) => {
        state.updateStatus.status = "loading";
        state.updateStatus.error = null;
      })
      .addCase(updateOrderProcessingStatus.fulfilled, (state) => {
        state.updateStatus.status = "success";
        state.updateStatus.error = null;
      })
      .addCase(updateOrderProcessingStatus.rejected, (state, action) => {
        state.updateStatus.status = "failed";
        state.updateStatus.error =
          (action.payload as string) || "Failed to update status";
      });
  },
});

export const { resetUpdateStatus, clearSingleOrder } =
  orderProcessingSlice.actions;
export default orderProcessingSlice.reducer;
